import React, { useState, useEffect, useContext } from 'react';
import { forumService } from '../services/apiClient';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { ChevronDown } from 'lucide-react';

const ForumPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clubFilter = queryParams.get('club');
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Doubt');
  const [expandedPost, setExpandedPost] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await forumService.getAllPosts();
      let allPosts = response.data.posts || [];
      if (clubFilter) {
        allPosts = allPosts.filter(p => p.club?._id === clubFilter || p.club === clubFilter);
      }
      setPosts(allPosts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      await forumService.createPost({ title, description, category });
      setSubmitted(true);
      setTitle('');
      setDescription('');
      setCategory('Doubt');
      setTimeout(() => setSubmitted(false), 3000);
      fetchPosts();
    } catch (error) {
      alert('Failed to create post: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReply = async (postId) => {
    if (!replyText[postId]) return;
    try {
      await forumService.replyToPost(postId, { content: replyText[postId] });
      setReplyText(prev => ({ ...prev, [postId]: '' }));
      fetchPosts();
    } catch (error) {
      alert('Failed to add reply: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpvote = async (postId) => {
    try {
      await forumService.upvotePost(postId);
      fetchPosts();
    } catch (error) {
      alert('Failed to upvote');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Create Post Form */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Forum</h1>

          {submitted && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              ✓ Post created successfully!
            </div>
          )}

          <form onSubmit={handleCreatePost} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-bold mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's your doubt or suggestion?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-bold mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide more details..."
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-bold mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              >
                <option>Doubt</option>
                <option>Suggestion</option>
                <option>General</option>
              </select>
            </div>

            <button
              type="submit"
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors"
            >
              Post Doubt/Suggestion
            </button>
          </form>
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post._id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div
                className="p-6 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedPost(expandedPost === post._id ? null : post._id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800">{post.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      By {post.author?.name} • {post.replies?.length || 0} replies
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-semibold">
                    {post.category}
                  </span>
                </div>
              </div>

              {expandedPost === post._id && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <p className="text-gray-700 mb-4">{post.description}</p>

                  <div className="flex gap-4 mb-6">
                    <button
                      onClick={() => handleUpvote(post._id)}
                      className="text-purple-600 hover:text-purple-700 font-semibold"
                    >
                      👍 {post.upvoteCount || 0}
                    </button>
                    <span className="text-gray-600">👁️ {post.views || 0} views</span>
                  </div>

                  {/* Replies */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-800 mb-4">Replies</h4>
                    <div className="space-y-3">
                      {post.replies?.map((reply, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                          <p className="text-sm font-semibold text-gray-800">{reply.author?.name}</p>
                          <p className="text-gray-600 text-sm mt-1">{reply.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add Reply - Only for Admins */}
                  {(user?.role === 'ClubAdmin' || user?.role === 'SuperAdmin') ? (
                    <div className="flex gap-2">
                      <textarea
                        value={replyText[post._id] || ''}
                        onChange={(e) => setReplyText(prev => ({ ...prev, [post._id]: e.target.value }))}
                        placeholder="Add a reply..."
                        rows="2"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                      />
                      <button
                        onClick={() => handleReply(post._id)}
                        className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700"
                      >
                        Reply
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-sm text-blue-700">Only Club Admins can reply to queries. Your post will be answered soon!</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ForumPage;
